import { SEPARATOR, type MenuItem } from "@shared/ui/Menu";
import { reminderChoices } from "@features/message-reminder";

/**
 * Меню сообщения по правой кнопке — по образцу Discord.
 *
 * Те же действия, что в ряду над строкой, и сверх них то, чему в ряду места
 * нет: копирование и пункты, которых ещё нет в продукте (помечены планом).
 * Ряд есть только под курсором, а меню открывается и долгим нажатием — на
 * касании другого пути к действиям над сообщением нет.
 *
 * Чего нет по праву, того нет вовсе: без права писать нет ни ответа, ни ветки.
 */
export function messageMenu({
  own,
  editable = own,
  forwardable = true,
  canDeleteOthers,
  canReact,
  canSend,
  canPin,
  pinned,
  saved = false,
  reminded = false,
  hasThread,
  content,
  selection,
  apps = [],
  actions,
}: {
  own: boolean;
  /** Можно ли править: своё, кроме голосового. По умолчанию — своё. */
  editable?: boolean;
  /** Можно ли переслать: эфемерное и опрос — нельзя, как в Discord. По умолчанию — можно. */
  forwardable?: boolean;
  canDeleteOthers: boolean;
  canReact: boolean;
  canSend: boolean;
  /** Можно ли закреплять в этом канале: на сервере — право управлять сообщениями. */
  canPin: boolean;
  pinned: boolean;
  /** В своих сохранённых. */
  saved?: boolean;
  /** О сообщении уже стоит своё напоминание. */
  reminded?: boolean;
  /** У сообщения уже есть ветка: пункт её открывает, а не заводит. */
  hasThread: boolean;
  content: string;
  /** Выделенное в этой строке на момент щелчка. */
  selection: string;
  /** Команды ботов на сообщение — подменю «Приложения», как в Discord. */
  apps?: MenuItem[];
  actions: {
    pickEmoji: () => void;
    edit: () => void;
    reply: () => void;
    /** «Создать ветку» / «Открыть ветку»; нет — пункта нет (личные сообщения, как в Discord). */
    thread?: () => void;
    copy: (text: string) => void;
    /** Ссылка на это сообщение — в буфер. */
    copyLink: () => void;
    forward: () => void;
    pin: () => void;
    /** «Опубликовать» объявление; нет — пункта нет. */
    publish?: () => void;
    /** Сохранить или убрать из сохранённых; нет — пункта нет (ветка или мест нет). */
    save?: () => void;
    /**
     * «Создать напоминание» со сроками, как в Discord; `null` — «Другое…»,
     * срок выбирают в окне. Нет — пункта нет (ветка или системное).
     */
    remind?: (at: Date | null) => void;
    /** «Удалить напоминание»; есть, только когда оно стоит. */
    forget?: () => void;
    /** «Озвучить сообщение»; нет — пункта нет (текста нет или голоса у системы). */
    speak?: () => void;
    /** «Реакции» — кто что поставил; нет — реакций нет. */
    reactions?: () => void;
    /** «Отметить непрочитанным»; нет — пункта нет (в ветке у отметки свой счёт). */
    markUnread?: () => void;
    remove: () => void;
  };
}): MenuItem[] {
  const groups: MenuItem[][] = [];

  const react: MenuItem[] = [];
  if (canReact) react.push({ label: "Добавить реакцию", onPick: actions.pickEmoji });
  if (actions.reactions) react.push({ label: "Реакции", onPick: actions.reactions });
  if (react.length) groups.push(react);

  const acts: MenuItem[] = [];
  if (actions.publish) acts.push({ label: "Опубликовать", onPick: actions.publish });
  if (editable) acts.push({ label: "Изменить", onPick: actions.edit });
  if (canSend) acts.push({ label: "Ответить", onPick: actions.reply });
  if (forwardable) acts.push({ label: "Переслать", onPick: actions.forward });
  if (canSend && actions.thread) acts.push({ label: hasThread ? "Открыть ветку" : "Создать ветку", onPick: actions.thread });
  groups.push(acts);

  const copy: MenuItem[] = [];
  if (selection) copy.push({ label: "Копировать", hint: "Ctrl+C", onPick: () => actions.copy(selection) });
  if (content) copy.push({ label: "Скопировать текст", onPick: () => actions.copy(content) });
  if (actions.speak) copy.push({ label: "Озвучить сообщение", onPick: actions.speak });
  if (canPin) copy.push({ label: pinned ? "Открепить" : "Закрепить", onPick: actions.pin });
  if (actions.save) copy.push({ label: saved ? "Убрать из сохранённых" : "Сохранить", onPick: actions.save });
  if (actions.remind) {
    const remind = actions.remind;
    copy.push({
      label: reminded ? "Изменить напоминание" : "Создать напоминание",
      items: [
        ...reminderChoices(new Date()).map((choice) => ({ label: choice.label, onPick: () => remind(choice.at) })),
        { label: "Другое…", onPick: () => remind(null) },
      ],
    });
    if (reminded && actions.forget) copy.push({ label: "Удалить напоминание", onPick: actions.forget });
  }
  if (actions.markUnread) copy.push({ label: "Отметить непрочитанным", onPick: actions.markUnread });
  copy.push({ label: "Скопировать ссылку", onPick: actions.copyLink });
  groups.push(copy);

  if (apps.length) groups.push([{ label: "Приложения", items: apps }]);

  if (own || canDeleteOthers) groups.push([{ label: "Удалить", kind: "danger", onPick: actions.remove }]);

  return groups.flatMap((group, index) => (index ? [SEPARATOR, ...group] : group));
}
